/**
 * Which renderer this device gets.
 *
 * Three signals, in order of trustworthiness:
 *
 *   window.tizen     the platform object only a real widget has. Definitive.
 *   UA contains Tizen  covers the TV browser and the case where the widget's platform
 *                    object is late to appear.
 *   ?tv=1            an explicit override, and the reason it exists is practical: the TV
 *                    layout has to be developable in a desktop browser. Waiting on an
 *                    `sdb install` round trip to see a focus-ring change would make the
 *                    layout cost twenty times what it should.
 *
 * The result is fixed for the lifetime of the page. Nothing here can change mid-session,
 * and re-evaluating would mean the whole tree could swap renderers under a running
 * player.
 */
import { useMemo } from 'react';

export type DeviceProfile = 'tv' | 'web';

declare global {
  interface Window {
    tizen?: {
      tvinputdevice?: {
        registerKey(name: string): void;
        unregisterKey(name: string): void;
        getSupportedKeys?(): { name: string; code: number }[];
      };
      application?: {
        getCurrentApplication(): { exit(): void };
      };
    };
    webapis?: { avplay?: Avplay };
  }
}

/**
 * The subset of AVPlay this app uses.
 *
 * Hand-written rather than pulled from a types package because there is no official one
 * and the community ones disagree with the panel. Every member here was checked against
 * the Tizen 9.0 documentation for the TV profile; anything optional is marked so, because
 * the API differs across platform versions and a missing method must be a compile-time
 * concern rather than a black screen.
 */
/**
 * One entry from `getTotalTrackInfo()`.
 *
 * `extra_info` is documented as a JSON string carrying the language and label, and it is
 * typed as `unknown` here rather than as a parsed object because that is what the panel
 * actually hands back — pretending otherwise would let a `JSON.parse` be skipped at a call
 * site and fail at three metres instead of at compile time.
 */
export interface AvplayTrackInfo {
  index: number;
  type: 'AUDIO' | 'VIDEO' | 'TEXT';
  extra_info: unknown;
}

export interface Avplay {
  open(url: string): void;
  close(): void;
  prepareAsync(onSuccess: () => void, onError: (e: unknown) => void): void;
  play(): void;
  pause(): void;
  stop(): void;
  seekTo(ms: number): void;
  getDuration(): number;
  setDisplayRect(x: number, y: number, width: number, height: number): void;
  /** Absent on some older platform versions; letterboxing is a preference, not a need. */
  setDisplayMethod?(mode: string): void;
  /**
   * Track enumeration and selection. Optional for the same reason as `setDisplayMethod`:
   * they are not present on every Tizen version, and a missing one must be a compile-time
   * concern rather than a crash on a panel nobody tested on.
   */
  getTotalTrackInfo?(): AvplayTrackInfo[];
  setSelectTrack?(type: 'AUDIO' | 'TEXT', index: number): void;
  /**
   * Whether the platform swallows subtitle cues instead of handing them to the app.
   *
   * AVPlay never draws text itself — it decodes on a hardware plane the DOM cannot reach,
   * so there is no surface for it to draw on. Selecting a TEXT track therefore displays
   * nothing at all unless the app both turns this off *and* renders the cues that arrive
   * through `onsubtitlechange`. Its presence is the capability check for offering
   * subtitles on this engine: without it a subtitle menu would be a control that appears
   * to work and does nothing.
   */
  setSilentSubtitle?(silent: boolean): void;
  setListener(listener: {
    oncurrentplaytime?(ms: number): void;
    onstreamcompleted?(): void;
    onerror?(type: string): void;
    onbufferingcomplete?(): void;
    /** `text` is one cue; `durationMs` is how long the platform says to keep it up. */
    onsubtitlechange?(durationMs: number, text: string): void;
  }): void;
}

function detect(): DeviceProfile {
  if (typeof window === 'undefined') return 'web';

  const override = new URLSearchParams(window.location.search).get('tv');
  if (override === '1') return 'tv';
  if (override === '0') return 'web';

  if (window.tizen) return 'tv';
  // Measured on the target panel: "Mozilla/5.0 (SMART-TV; LINUX; Tizen 9.0) …"
  if (/\bTizen\b/i.test(navigator.userAgent)) return 'tv';

  return 'web';
}

/** Stable for the life of the page — see the header for why that is deliberate. */
const PROFILE: DeviceProfile = detect();

export function useDeviceProfile(): DeviceProfile {
  return useMemo(() => PROFILE, []);
}

export function isTv(): boolean {
  return PROFILE === 'tv';
}

/**
 * Whether AVPlay is usable.
 *
 * Separate from the device profile because they answer different questions: the profile
 * decides which UI to render, this decides which playback engine can be offered. The TV
 * browser is `tv` for layout purposes and has no AVPlay at all.
 */
export function hasAvplay(): boolean {
  return typeof window !== 'undefined' && Boolean(window.webapis?.avplay);
}
